import { useState, useRef, useCallback, useEffect } from 'react';
import {
  type AudioController,
  type AudioControllerOptions,
  type StrategyController,
  type BufferUpdateData,
  usePlaybackEvents,
  initDirectAudio,
  initHlsAudio,
} from './strategies';
import { getHlsType, type AudioType } from '../../utils/deviceInfo';

export type { BufferUpdateData };

export function useAudioController({
  initialPlaybackRate = 1,
  callbacks,
}: AudioControllerOptions): AudioController {
  // State
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayRequested, setIsPlayRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playbackRate, setPlaybackRateState] = useState(initialPlaybackRate);

  // Core refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const strategyRef = useRef<StrategyController | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldAutoPlayRef = useRef(false);
  const isPlayRequestedRef = useRef(false);
  const currentContentIdRef = useRef<string | null>(null);
  const pendingInitRef = useRef<{
    contentId: string;
    startPosition: number;
    autoPlay: boolean;
  } | null>(null);
  const callbacksRef = useRef(callbacks);
  const isPlayingRef = useRef(false);
  const playbackRateRef = useRef(playbackRate);
  const audioTypeRef = useRef<AudioType>('single-file');
  // Track if current action was user-initiated (for suppressing error toasts on background init)
  const userInitiatedRef = useRef(false);

  // Keep refs up to date
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    isPlayRequestedRef.current = isPlayRequested;
  }, [isPlayRequested]);

  // Playback events hook
  const {
    setPlaylistId,
    trackListeningTime,
    sendPlaybackStart,
    sendPlaybackStop,
    sendPlaybackSeek,
    sendPlaybackComplete,
  } = usePlaybackEvents({
    audioRef,
    currentContentIdRef,
    isPlayingRef,
    audioTypeRef,
  });

  // Cleanup function
  const cleanup = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    shouldAutoPlayRef.current = false;
    strategyRef.current?.cleanup();
    strategyRef.current = null;
  }, []);

  // Play audio
  const play = useCallback(async (): Promise<void> => {
    if (!audioRef.current) return;

    // Mark as user-initiated for error handling
    userInitiatedRef.current = true;

    try {
      await audioRef.current.play();
      setIsPlaying(true);
      isPlayingRef.current = true;
      setIsLoading(false);
      setIsPlayRequested(false);
      sendPlaybackStart();
    } catch (err) {
      console.error('[Audio] Playback failed:', err);
      setIsLoading(false);
      setIsPlaying(false);
      isPlayingRef.current = false;
      setIsPlayRequested(false);
      setError('Playback failed');
      callbacksRef.current.onError('Playback failed');
    }
  }, [sendPlaybackStart]);

  // Initialize audio source
  const initializeSource = useCallback(
    async (
      contentId: string,
      startPosition: number,
      autoPlay = false
    ): Promise<void> => {
      // Reset user-initiated flag - init is automatic, not user action
      userInitiatedRef.current = false;

      // Reset state (preserve position if reinitializing)
      setIsLoading(true);
      setIsPlaying(false);
      setIsPlayRequested(autoPlay);
      setError(null);
      setCurrentTime(startPosition);

      cleanup();

      // Set autoplay AFTER cleanup (which resets it)
      shouldAutoPlayRef.current = autoPlay;

      // If the <audio> element isn't mounted yet, queue the init and retry ASAP.
      // This prevents early play presses from being silently ignored during page init.
      if (!audioRef.current) {
        pendingInitRef.current = { contentId, startPosition, autoPlay };
        return;
      }

      if (contentId === currentContentIdRef.current) return;
      currentContentIdRef.current = contentId;

      audioRef.current.pause();
      audioRef.current.removeAttribute('src');

      // Check if content is single file or HLS, and wait until ready
      // No timeout during this phase - we're waiting for audio generation
      let useSingleFile = false;
      const MAX_READY_POLLS = 60; // Wait up to 60 seconds for first audio
      const POLL_INTERVAL_MS = 1000;

      for (let poll = 0; poll < MAX_READY_POLLS; poll++) {
        try {
          const checkRes = await fetch('/api/buffer-check', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contentId, position: 0 }),
          });
          if (checkRes.status === 403) {
            const data = await checkRes.json().catch(() => ({}));
            if (data?.requiresProPdf) {
              callbacksRef.current.onLimit?.('pdf');
            } else if (data?.requiresLogin) {
              callbacksRef.current.onLimit?.('signin');
            } else {
              callbacksRef.current.onLimit?.('upgrade');
            }
            setIsLoading(false);
            setError(data?.error || 'Listening limit reached');
            return;
          }
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            useSingleFile = checkData.isSingleFile;
            // Update buffer state with current status
            if (callbacksRef.current.onBufferUpdate) {
              callbacksRef.current.onBufferUpdate({
                secondsGenerated: checkData.secondsGenerated,
                secondsTotal: checkData.secondsTotal,
                status: checkData.status,
              });
            }
            // If ready (has audio) or failed, proceed
            if (checkData.isReady || checkData.status === 'failed') {
              break;
            }
            // Not ready yet, wait and poll again
            await new Promise((resolve) =>
              setTimeout(resolve, POLL_INTERVAL_MS)
            );
          } else {
            break; // Server error, proceed anyway
          }
        } catch {
          break; // Network error, proceed anyway
        }
      }

      // Now set loading timeout for actual audio initialization (after polling)
      loadingTimeoutRef.current = setTimeout(() => {
        console.error('[Audio] Loading timeout - audio failed to initialize');
        setIsLoading(false);
        setIsPlayRequested(false);
        setError('Audio loading timed out');
        // Only show error toast if this was user-initiated (e.g., user pressed play)
        // Suppress toast for background auto-initialization failures
        if (userInitiatedRef.current) {
          callbacksRef.current.onError('Audio loading timed out');
        }
      }, 10000);

      const hlsSourceUrl = `/api/stream?id=${contentId}`;
      const directSourceUrl = `/api/stream?id=${contentId}&direct=1`;

      // Shared event handlers
      const onCanPlay = () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }

        // Set position BEFORE playing to avoid brief playback from 0
        if (startPosition > 0 && audioRef.current) {
          audioRef.current.currentTime = startPosition;
          setCurrentTime(startPosition);
        }

        setIsLoading(false);

        if (shouldAutoPlayRef.current && audioRef.current) {
          shouldAutoPlayRef.current = false;
          play();
        }
      };

      const onEnded = () => {
        sendPlaybackComplete();
        isPlayingRef.current = false;
        callbacksRef.current.onEnded();
      };

      const onWaiting = () => {
        if (isPlayingRef.current) {
          setIsLoading(true);
        }
      };

      const onPlaying = () => {
        if (isPlayingRef.current) {
          setIsLoading(false);
          setIsPlaying(true);
        }
      };

      const onTimeUpdate = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
        trackListeningTime();
      };

      const handlers = {
        onCanPlay,
        onEnded,
        onWaiting,
        onPlaying,
        onTimeUpdate,
      };

      // Initialize with appropriate strategy
      if (useSingleFile) {
        audioTypeRef.current = 'single-file';
        strategyRef.current = initDirectAudio(
          { audioRef, playbackRate },
          directSourceUrl,
          handlers
        );
      } else {
        audioTypeRef.current = getHlsType();
        strategyRef.current = initHlsAudio(
          {
            audioRef,
            isPlayingRef,
            playbackRate,
            contentId,
            loadingTimeoutRef,
            callbacksRef,
            setIsLoading,
            setError,
          },
          hlsSourceUrl,
          startPosition,
          handlers
        );
      }

      // Ensure playbackRate is synced using ref (in case it changed since callback was created)
      strategyRef.current?.setPlaybackRate(playbackRateRef.current);
    },
    [cleanup, play, playbackRate, trackListeningTime, sendPlaybackComplete]
  );

  // If initialization was requested before the audio element existed, retry once it mounts.
  // Note: a ref assignment doesn't re-render, but any early play press sets state, so we
  // get a render/commit cycle where the ref is typically attached by the time this runs.
  useEffect(() => {
    if (!audioRef.current) return;
    const pending = pendingInitRef.current;
    if (!pending) return;
    pendingInitRef.current = null;
    initializeSource(
      pending.contentId,
      pending.startPosition,
      pending.autoPlay
    );
  }, [initializeSource, isLoading]);

  // Pause
  const pause = useCallback(() => {
    if (!audioRef.current) return;

    sendPlaybackStop();
    setIsLoading(false); // Clear loading state (e.g., if user taps spinner to cancel)
    setIsPlaying(false);
    isPlayingRef.current = false;
    shouldAutoPlayRef.current = false;
    setIsPlayRequested(false);
    audioRef.current.pause();
  }, [sendPlaybackStop]);

  const seek = useCallback(
    (time: number) => {
      if (!audioRef.current) return;

      const wasPlaying = isPlayingRef.current;

      if (wasPlaying) {
        sendPlaybackSeek(time);
      }

      const seekTime = time === 0 ? 0.001 : time;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(time);

      // Only call play() if audio actually paused during seek
      // (e.g., due to buffering). If still playing, no need to call play().
      if (wasPlaying && audioRef.current.paused) {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            isPlayingRef.current = true;
          })
          .catch((err) => {
            console.error('[Audio] Play after seek failed:', err);
          });
      }
    },
    [sendPlaybackSeek]
  );

  // Set playback rate
  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackRateState(rate);
    strategyRef.current?.setPlaybackRate(rate);
  }, []);

  // Force reinitialization (e.g., when switching from HLS to single-file mode)
  const reinitialize = useCallback(() => {
    const contentId = currentContentIdRef.current;
    const position = audioRef.current?.currentTime ?? 0;
    const wasPlaying = isPlayingRef.current;

    if (!contentId) return;

    // Clear content ID to force reinitialization
    currentContentIdRef.current = null;

    // Defer to next tick to allow current strategy to finish
    setTimeout(async () => {
      await initializeSource(contentId, position, wasPlaying);
    }, 0);
  }, [initializeSource]);

  // Destroy
  const destroy = useCallback(() => {
    cleanup();
    currentContentIdRef.current = null;
    isPlayingRef.current = false;
    setCurrentTime(0);
    setIsLoading(false);
    setIsPlaying(false);
    setIsPlayRequested(false);
    setError(null);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Note: Visibility change handling is done in usePlaybackEvents.ts
  // to avoid duplicate recovery attempts

  return {
    currentTime,
    isLoading,
    isPlaying,
    isPlayRequested,
    error,
    playbackRate,
    initializeSource,
    play,
    pause,
    seek,
    setPlaybackRate,
    setPlaylistId,
    reinitialize,
    destroy,
    audioRef,
  };
}
